import React, { useState } from "react";
import { StatCard, CompactComparisonRow } from "../../../../../globalComponents/ui/Card";
import SparkBar from "../../../../../globalComponents/ui/SparkBar";
import { DeltaBadge } from "../../../../../globalComponents/ui/Badge";
import TabBar from "../../../../../globalComponents/ui/TabBar";
import { PrimaryButton, SecondaryButton } from "../../../../../globalComponents/ui/Button";
import { KpiIcon } from "../KpiIcons";
import TriggerSection from "./TriggerSection";
import AccessRequestActions from "./AccessRequestActions";
import { LOCKED, OPEN } from "../../../KpiLock/helpers";
import { formatNumber } from "../../../../../utils/numberFormat";
import {
  PanelContent,
  OverviewColumn,
  StatGrid,
  YoYSection,
  YoYTitle,
  YoYRow,
  YoYItem,
  YoYItemHeader,
  YoYLabel,
  YoYValue,
  YoYDelta,
  YoYDeltaLabel,
  ActionRow,
} from "./KpiRow.styled";

const TABS = [{ key: "overview", label: "Overview" }];

/**
 * Accordion panel content — overview tab with stat cards, YoY comparison,
 * compact type rows (emission/energy/NOx/SOx/PPM), triggers, and action buttons.
 */
const KpiRowOverview = ({
  item,
  analytics,
  kpiTypes,
  matchingTriggers,
  triggersFallback,
  projection,
  emissionMode,
  hasPermission,
  lock = { status: OPEN, dueDate: null, lockedLocationIds: [] },
  accessRequested = false,
  accessDeclined = false,
  requestingAccess = false,
  onRequestAccess,
  canDecideAccess = false,
  pendingRequestIds = [],
  pendingRequestLocationIds = [],
  onApproveAccess,
  onRejectAccess,
  canAddAndEditRecords,
  subQuestions,
  onAdd,
  onHistory,
  onViewSubQuestions,
}) => {
  const [tab, setTab] = useState("overview");

  const { currentFY, previousFY, currentSelected, previousSelected } = analytics;
  const maxVal = Math.max(currentFY, previousFY, 1);

  const hasEmission = kpiTypes.includes("EMISSION");
  const hasEnergy = kpiTypes.includes("ENERGY");
  const hasNOx = kpiTypes.includes("NOX");
  const hasSOx = kpiTypes.includes("SOX");
  const hasPPM = kpiTypes.includes("PPM");

  const COMPACT_ROWS = [
    { show: hasEmission, icon: "🌿", label: "Emission", cur: analytics.emissionCurrent, prev: analytics.emissionPrevious, unit: analytics.emissionUnit, accent: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" },
    { show: hasEnergy, icon: "⚡", label: "Energy", cur: analytics.energyCurrent, prev: analytics.energyPrevious, unit: analytics.energyUnit, accent: "#b45309", bg: "#fffbeb", border: "#fde68a" },
    { show: hasNOx, icon: "💨", label: "NOx", cur: analytics.noxCurrent, prev: analytics.noxPrevious, unit: "mg/Nm³", accent: "#b91c1c", bg: "#fef2f2", border: "#fecaca" },
    { show: hasSOx, icon: "🟡", label: "SOx", cur: analytics.soxCurrent, prev: analytics.soxPrevious, unit: "mg/Nm³", accent: "#a16207", bg: "#fefce8", border: "#fef08a" },
    { show: hasPPM, icon: "🌫️", label: "PPM", cur: analytics.ppmCurrent, prev: analytics.ppmPrevious, unit: "mg/Nm³", accent: "#6b21a8", bg: "#faf5ff", border: "#e9d5ff" },
  ];

  return (
    <PanelContent>
      <TabBar tabs={TABS} activeKey={tab} onChange={setTab} />

      {tab === "overview" && (
        <OverviewColumn>
          {/* Compact rows shown ABOVE stat cards in emissionMode */}
          {emissionMode && COMPACT_ROWS.filter((r) => r.show && (r.label === "Emission" || r.label === "Energy")).map((r) => (
            <CompactComparisonRow key={r.label} icon={r.icon} label={r.label} currentVal={r.cur} previousVal={r.prev} unit={r.unit} accentColor={r.accent} bgColor={r.bg} borderColor={r.border} />
          ))}

          {/* Stat cards grid */}
          <StatGrid>
            <StatCard label="Current FY (Submitted)" value={currentFY} unit={item.unit} accent="var(--accent)" bg="var(--accent-3)" />
            <StatCard label="Previous FY (Submitted)" value={previousFY} unit={item.unit} accent="var(--purple)" bg="var(--purple-2)" />
            <StatCard label="Current FY (Selected Range)" value={currentSelected} unit={item.unit} accent="var(--amber)" bg="var(--amber-2)" />
            <StatCard label="Previous FY (Selected Range)" value={previousSelected} unit={item.unit} accent="var(--red)" bg="var(--red-2)" />
          </StatGrid>

          {/* YoY comparison */}
          <YoYSection>
            <YoYTitle>Year-over-Year Comparison</YoYTitle>
            <YoYRow>
              {[
                { label: "Current FY", val: currentFY, color: "var(--accent)" },
                { label: "Previous FY", val: previousFY, color: "var(--purple)" },
              ].map(({ label, val, color }) => (
                <YoYItem key={label}>
                  <YoYItemHeader>
                    <YoYLabel>{label}</YoYLabel>
                    <YoYValue $color={color}>{formatNumber(val)} {item.unit}</YoYValue>
                  </YoYItemHeader>
                  <SparkBar value={val} max={maxVal} color={color} />
                </YoYItem>
              ))}
            </YoYRow>
            <YoYDelta>
              <YoYDeltaLabel>Change vs Previous FY:</YoYDeltaLabel>
              <DeltaBadge current={currentFY} previous={previousFY} />
            </YoYDelta>
          </YoYSection>

          {/* Compact rows shown BELOW in non-emissionMode */}
          {!emissionMode && COMPACT_ROWS.filter((r) => r.show).map((r) => (
            <CompactComparisonRow key={r.label} icon={r.icon} label={r.label} currentVal={r.cur} previousVal={r.prev} unit={r.unit} accentColor={r.accent} bgColor={r.bg} borderColor={r.border} />
          ))}

          {/* Trigger section */}
          <TriggerSection matchingTriggers={matchingTriggers} isFallback={triggersFallback} projection={projection} />

          {/* Action buttons */}
          <ActionRow>
            {!emissionMode && !item.isFormulaBased && hasPermission("quantitative-data.create") && (
              <PrimaryButton
                style={{ height: 32, padding: "0 14px", fontSize: 12, minWidth: "auto" }}
                disabled={lock.status === LOCKED}
                title={lock.status === LOCKED ? `Closed for entry on ${lock.dueDate}` : undefined}
                onClick={(e) => { e.stopPropagation(); if (lock.status !== LOCKED) onAdd?.(item); }}
              >
                <KpiIcon name="add" size={11} color="#fff" /> Add Data
              </PrimaryButton>
            )}
            {hasPermission("quantitative-data.view") && (
              <SecondaryButton style={{ height: 32, padding: "0 14px", fontSize: 12, minWidth: "auto" }} onClick={(e) => { e.stopPropagation(); onHistory?.(item); }}>
                <KpiIcon name="history" size={11} /> History
              </SecondaryButton>
            )}
            <AccessRequestActions
              item={item}
              lock={lock}
              accessRequested={accessRequested}
              accessDeclined={accessDeclined}
              requestingAccess={requestingAccess}
              onRequestAccess={onRequestAccess}
              canDecideAccess={canDecideAccess}
              pendingRequestIds={pendingRequestIds}
              pendingRequestLocationIds={pendingRequestLocationIds}
              onApproveAccess={onApproveAccess}
              onRejectAccess={onRejectAccess}
            />
            {subQuestions.length > 0 && (
              <SecondaryButton style={{ height: 32, padding: "0 14px", fontSize: 12, minWidth: "auto" }} onClick={(e) => { e.stopPropagation(); onViewSubQuestions?.(); }}>
                View Sub Questions
              </SecondaryButton>
            )}
          </ActionRow>
        </OverviewColumn>
      )}
    </PanelContent>
  );
};

export default KpiRowOverview;
